import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, LogIn, UserPlus, Loader2, ArrowLeft, Sparkles, Eye, EyeOff } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, dashboardFor } from '../lib/auth';
import { api } from '../lib/api';
import { toast } from 'sonner';
import TurnstileWidget, { turnstileConfigured } from '../components/TurnstileWidget';
import { useLang } from '../context/LanguageContext';

export default function LoginPage({ defaultMode = 'login' }) {
  const { t } = useLang();
  const { user, login, signup, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const next = params.get('next');
  const inviteToken = params.get('invite') || '';

  const [mode, setMode] = useState(defaultMode);
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [tsToken, setTsToken] = useState('');
  const [loginShowsCaptcha, setLoginShowsCaptcha] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('wyse_remember') === '1');
  const captchaRef = useRef(null);

  useEffect(() => {
    if (!inviteToken) return;
    setMode('signup');
    api.lookupInvitation(inviteToken).then((d) => {
      if (d.kind !== 'member') { setInviteError(t('login.inviteErr1')); return; }
      if (d.expired) { setInviteError(t('login.inviteErr2')); return; }
      if (d.status === 'accepted') { setInviteError(t('login.inviteErr3')); return; }
      if (d.status === 'revoked') { setInviteError(t('login.inviteErr4')); return; }
      setInvite(d);
      setForm((f) => ({ ...f, email: d.email, name: d.name || f.name }));
    }).catch(() => setInviteError(t('login.inviteErr5')));
  }, [inviteToken]);

  if (!loading && user) {
    return <Navigate to={next || dashboardFor(user.role)} replace />;
  }

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    // Signup always requires Turnstile; Login only after server flags suspicious attempts.
    const needCaptcha = turnstileConfigured && (mode === 'signup' || loginShowsCaptcha);
    if (needCaptcha && !tsToken) {
      toast.error(t('cf.errCaptcha'));
      return;
    }
    setSubmitting(true);
    try {
      const u = mode === 'login'
        ? await login(form.email, form.password, tsToken)
        : await signup({
            email: form.email, password: form.password,
            name: form.name, phone: form.phone,
            invite_token: invite ? inviteToken : '',
          }, tsToken);
      toast.success(mode === 'login' ? `${t('login.welcomeToast')} ${u.name?.split(' ')[0] || ''}` : t('login.acctCreated'));
      nav(next || dashboardFor(u.role));
    } catch (err) {
      const detail = err?.response?.data?.detail || (mode === 'login' ? t('login.errLogin') : t('login.errSignup'));
      toast.error(detail);
      // If the backend says we need a captcha, surface it.
      if (mode === 'login' && /verification|too many recent/i.test(detail)) {
        setLoginShowsCaptcha(true);
      }
      setTsToken(''); captchaRef.current?.reset();
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <Header />
      <section className="min-h-[calc(100vh-160px)] bg-gradient-to-b from-[#fbf7ee] to-white py-16 flex items-center">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
          <div className="hidden lg:block">
            <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-[#c89200] mb-8"><ArrowLeft className="w-4 h-4" /> {t('login.back')}</Link>
            <p className="section-eyebrow mb-4">{t('login.eyebrow')}</p>
            <h1 className="text-[42px] leading-[1.05] font-extrabold text-[#14110f]">
              {t('login.heroTitle1')} <span className="text-[#f5b300]">{t('login.heroTitle2')}</span>
            </h1>
            <p className="mt-5 text-[15px] text-neutral-600 leading-relaxed max-w-md">
              {t('login.heroDesc')}
            </p>
            <ul className="mt-7 space-y-3 text-[14px] text-[#14110f]">
              {[t('login.feat1'), t('login.feat2'), t('login.feat3'), t('login.feat4')].map((lbl) => (
                <li key={lbl} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#f5b300]" />
                  {lbl}
                </li>
              ))}
            </ul>
          </div>

          <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto bg-white rounded-xl shadow-[0_30px_70px_-30px_rgba(20,17,15,0.3)] p-8 md:p-10">
            <div className="flex rounded-md bg-[#faf6ec] p-1 mb-7">
              {['login', 'signup'].map((m) => (
                <button key={m} onClick={() => setMode(m)} className={`flex-1 py-2.5 rounded-md text-[13px] font-semibold uppercase tracking-wider transition ${mode === m ? 'bg-white text-[#14110f] shadow-sm' : 'text-neutral-500'}`}>
                  {m === 'login' ? t('login.signinTab') : t('login.signupTab')}
                </button>
              ))}
            </div>
            <h2 className="text-[26px] font-extrabold text-[#14110f]">{mode === 'login' ? t('login.welcomeBack') : t('login.createAcct')}</h2>
            <p className="mt-1.5 text-[13.5px] text-neutral-600">
              {mode === 'login' ? t('login.signinSub') : t('login.signupSub')}
            </p>
            {(invite || inviteError) && mode === 'signup' && (
              <div data-testid="invite-banner"
                   className={`mt-4 rounded-md px-3 py-2.5 text-[12.5px] flex items-start gap-2 ${
                     inviteError
                       ? 'bg-rose-50 text-rose-700 border border-rose-100'
                       : 'bg-amber-50 text-amber-800 border border-amber-100'
                   }`}>
                <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  {inviteError ? inviteError : (
                    <>{t('login.invited')}{invite?.invited_by_name ? <> {t('login.invitedBy')} <strong>{invite.invited_by_name}</strong></> : ''}. {t('login.invitedLock')}</>
                  )}
                </div>
              </div>
            )}
            <form onSubmit={submit} className="mt-6 space-y-3.5">
              {mode === 'signup' && (
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input required name="name" value={form.name} onChange={handle} placeholder={t('acct.fullName')} className="w-full pl-10 pr-4 py-3.5 rounded-md border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
                </div>
              )}
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input required type="email" name="email" value={form.email} onChange={handle}
                       readOnly={!!invite}
                       placeholder={t('login.emailPh')}
                       className={`w-full pl-10 pr-4 py-3.5 rounded-md border focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px] ${invite ? 'bg-neutral-50 text-neutral-500 border-neutral-200' : 'border-neutral-200'}`} />
              </div>
              {mode === 'signup' && (
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input name="phone" value={form.phone} onChange={handle} placeholder={t('login.phonePh')} className="w-full pl-10 pr-4 py-3.5 rounded-md border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
                </div>
              )}
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input data-testid="login-password" required
                       type={showPassword ? 'text' : 'password'}
                       name="password" value={form.password} onChange={handle}
                       placeholder={mode === 'signup' ? t('login.passwordCreatePh') : t('login.passwordPh')}
                       className="w-full pl-10 pr-11 py-3.5 rounded-md border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
                <button type="button" data-testid="password-visibility-toggle"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? t('login.hidePw') : t('login.showPw')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-[#14110f]">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === 'login' && (
                <div className="flex items-center justify-between text-[12.5px]">
                  <label className="inline-flex items-center gap-2 text-neutral-600 select-none cursor-pointer">
                    <input data-testid="remember-me" type="checkbox" checked={rememberMe}
                           onChange={(e) => {
                             setRememberMe(e.target.checked);
                             localStorage.setItem('wyse_remember', e.target.checked ? '1' : '0');
                           }}
                           className="w-3.5 h-3.5 accent-[#f5b300]" />
                    {t('login.remember')}
                  </label>
                  <Link data-testid="forgot-password-link" to="/forgot-password"
                        className="text-[#c89200] font-semibold hover:underline">
                    {t('login.forgot')}
                  </Link>
                </div>
              )}
              {(mode === 'signup' || loginShowsCaptcha) && (
                <TurnstileWidget ref={captchaRef}
                                 onVerify={setTsToken}
                                 onExpire={() => setTsToken('')}
                                 testId={mode === 'signup' ? 'signup-turnstile' : 'login-turnstile'} />
              )}
              <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-7 py-3.5 rounded-md shadow-[0_14px_30px_-10px_rgba(245,179,0,0.7)] transition-all">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'login' ? <LogIn className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />)}
                {mode === 'login' ? t('login.signinBtn') : t('login.createBtn')}
              </button>
              <p className="text-[12.5px] text-center text-neutral-500">
                {mode === 'login' ? (<>{t('login.newHere')} <button type="button" onClick={() => setMode('signup')} className="text-[#c89200] font-semibold hover:underline">{t('login.createOne')}</button></>)
                  : (<>{t('login.haveAcct')} <button type="button" onClick={() => setMode('login')} className="text-[#c89200] font-semibold hover:underline">{t('login.signinBtn')}</button></>)}
              </p>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
